import React, { useEffect, useState, useRef } from 'react';
import Select, { components } from 'react-select';
import ICON_ID from '@gcloud-npm/utils.enums.icon-id';
import { COLOR_DICTIONARY } from '@gcloud-npm/utils.enums.colors';
import IconGlyph from '@/bit/components/components.icon-glyph';
import PropTypes from 'prop-types';
import { sendTaggingInfo } from '@/utils/common';
import InputNew, { COMPONENT_THEME } from '@/components/inputNew';
import Close from './close';
import customStyles from './customStyles';

function Input(props) {
  return (
    <InputNew
      {...props}
      inputRef={props.selectProps.inputRef}
      className="search-input__input"
      name={`${props.selectProps.name}`}
      id={`${props.selectProps.instanceId}-input`}
      label={props.selectProps.label}
      value={props.selectProps.inputValue}
      error={props.selectProps.error}
      showError={false}
      theme={COMPONENT_THEME.NO_BOX}
      autocomplete="off"
    />
  );
}
function DropdownIndicator(props) {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <IconGlyph
          id={ICON_ID.Buscar}
          alt={ICON_ID.Buscar}
          color={COLOR_DICTIONARY.GREY6}
          width={20}
          layoutSize={{ width: 20 }}
        />
      </components.DropdownIndicator>
    )
  );
}
function ClearIndicator(props) {
  const handleOnClick = () => {
    props.clearValue();
    if (props.selectProps.inputRef?.current) {
      props.selectProps.inputRef.current.focus();
    }
  };
  return (
    components.ClearIndicator && (
      <components.ClearIndicator {...props}>
        <Close
          handleOnClick={handleOnClick}
          componentToClear={props.selectProps.instanceId}
        />
      </components.ClearIndicator>
    )
  );
}

function SearchInput({
  className,
  placeholder,
  name,
  instanceId,
  searchRequest,
  options,
  onChange,
  value,
  error,
  onBlur,
  searching,
}) {
  const [inputText, setInputText] = useState('');
  const [optionSelected, setOptionSelected] = useState(value);
  const [focused, setFocused] = useState(false);
  const taggingInfo = {
    componentVariation: 'all',
    componentProduct: '',
    componentName: 'search-select',
    componentContent: placeholder,
  };

  useEffect(() => {
    if (typeof value === 'object') {
      setInputText(value?.label);
      setOptionSelected(value);
    }
  }, [value]);

  const handleInputChange = (text, meta) => {
    if (meta.action === 'input-change') {
      setInputText(text);
      onChange(text);
      searchRequest({ search: text });
    }
  };
  const handleSelectChange = (option) => {
    const { label = '' } = { ...option };
    onChange(option);
    if (label) {
      sendTaggingInfo(taggingInfo, 'select-option');
    }
  };
  const handleOnblur = () => {
    onBlur();
    setFocused(false);
  };
  const noOptionsMessage = ({ inputValue }) => {
    if (inputValue.trim().length === 0) {
      return null;
    }
    return (
      <span aria-live="assertive" role="status">
        No hay resultados
      </span>
    );
  };
  const loadingMessage = () => {
    return (
      <span aria-live="assertive" role="status">
        Buscando resultados...
      </span>
    );
  };
  const inputRef = useRef(null);
  return (
    <div className={`search-input ${className}`}>
      <Select
        inputRef={inputRef}
        value={optionSelected}
        instanceId={instanceId}
        id={instanceId}
        name={name}
        options={options}
        label={placeholder}
        isClearable={!!inputText}
        components={{
          IndicatorSeparator: () => null,
          LoadingIndicator: () => null,
          DropdownIndicator,
          ClearIndicator,
          SingleValue: () => null,
          Input,
          Placeholder: () => null,
        }}
        onFocus={() => setFocused(true)}
        onBlur={handleOnblur}
        isFocused={focused}
        styles={customStyles}
        inputValue={inputText}
        onInputChange={handleInputChange}
        onChange={handleSelectChange}
        filterOption={null}
        noOptionsMessage={noOptionsMessage}
        error={error}
        isLoading={searching}
        loadingMessage={loadingMessage}
        screenReaderStatus={() => ''} 
      />
      {error && (
        <p
          id={`${instanceId}-input-error`}
          role="alert"
          className="select__error"
        >
          {error}
        </p>
      )}
      <style jsx>
        {`
          @import '../../styles/colors.scss';
          .search-input {
            position: relative;
          }
          :global(.search-input__input) {
            padding-right: 32px;
          }
          .select__error {
            color: #c10000;
            font-family: Regular;
            font-size: 12px;
            padding: 4px 0 0 12px;
          }
          :global(.search-input__label) {
            font-family: Regular;
            color: $color-gp--grey4;
            position: absolute;
            font-size: 16px;
          }
          :global(.search-input__label--focused) {
            font-family: Regular;
            color: var(--color-gp--blue-a);
            position: absolute;
            font-size: 12px;
            top: 9px;
          }
          :global(.search-input__label--active) {
            font-family: Regular;
            color: $color-gp--grey4;
            position: absolute;
            font-size: 12px;
            top: 9px;
          }
          :global(.search-input__label--error) {
            font-family: Regular;
            color: #c10000;
          }
          :global(#button-textWithoutIcon) {
              z-index: 0 !important;
            }
          @media screen and (min-width: 768px) {
            :global(.search-input__label--focused) {
              top: 4px;
            }
            :global(.search-input__label--active) {
              top: 4px;
            }

            :global(#button-textWithoutIcon) {
              z-index: 0 !important;
            }
          }
        `}
      </style>
    </div>
  );
}

SearchInput.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  instanceId: PropTypes.string.isRequired,
  error: PropTypes.string,
  searchRequest: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  searching: PropTypes.bool,
};

SearchInput.defaultProps = {
  className: '',
  error: '',
  onChange: () => {},
  onBlur: () => {},
  placeholder: '',
  searching: false,
};
export default SearchInput;
